import { BASE_URL } from '../components/AppConfig';
import { getBearerToken } from '../utils/authToken';

const statusCodeOf = (res) => String(res?.statusCode || res?.status_code || '');

const rangeQuery = ({ from, to } = {}) => {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const query = params.toString();
  return query ? `?${query}` : '';
};

const authFetch = async (path, options = {}) => {
  const token = getBearerToken();
  if (!token) {
    return { status_code: '50000', statusCode: '50000', message: 'Not authenticated' };
  }

  const { headers: optionHeaders, ...rest } = options;

  try {
    const response = await fetch(`${BASE_URL}${path}`, {
      credentials: 'include',
      ...rest,
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
        ...(optionHeaders || {}),
      },
    });

    const data = await response.json().catch(() => ({}));
    const code = data?.status_code ?? data?.statusCode;
    if (!response.ok && (data == null || code == null)) {
      return {
        status_code: '50000',
        statusCode: '50000',
        message: data?.message || `Request failed (${response.status})`,
        httpStatus: response.status,
      };
    }

    return {
      ...data,
      statusCode: data.statusCode ?? data.status_code,
      status_code: data.status_code ?? data.statusCode,
      httpStatus: response.status,
    };
  } catch {
    return { status_code: '50000', statusCode: '50000', message: 'Request failed' };
  }
};

const authMultipartFetch = async (path, formData, options = {}) => {
  const token = getBearerToken();
  const { headers: optionHeaders, ...rest } = options;
  const headers = { ...(optionHeaders || {}) };
  if (token) headers.Authorization = token;

  try {
    const response = await fetch(`${BASE_URL}${path}`, {
      credentials: 'include',
      ...rest,
      headers,
      body: formData,
    });
    const data = await response.json().catch(() => ({}));
    const code = data?.status_code ?? data?.statusCode;
    if (!response.ok && (data == null || code == null)) {
      return {
        status_code: '50000',
        statusCode: '50000',
        message: data?.message || `Upload failed (${response.status})`,
        httpStatus: response.status,
      };
    }
    return {
      ...data,
      statusCode: data.statusCode ?? data.status_code,
      status_code: data.status_code ?? data.statusCode,
      httpStatus: response.status,
    };
  } catch {
    return { status_code: '50000', statusCode: '50000', message: 'Upload failed' };
  }
};

/** POST /admin/fan-scan/upload-image — multipart field `image`. Returns data.image_url. */
export const uploadFanScanBackground = (imageFile) => {
  const formData = new FormData();
  formData.append('image', imageFile);
  return authMultipartFetch('/admin/fan-scan/upload-image', formData, { method: 'POST' });
};

export const isFanScanApiSuccess = (res) => statusCodeOf(res) === '10000';

export const getFanScanPayload = (res) => res?.data ?? null;

export const isFanScanAuthError = (res) => statusCodeOf(res) === '10003';

export const isFanScanConflict = (res) =>
  res?.httpStatus === 409 || /approved/i.test(String(res?.message || ''));

export const getFanScanErrorMessage = (res, fallback) =>
  String(res?.message || '') || fallback;

/** GET /admin/fan-scan?from=&to= */
export const getFanScanDashboard = ({ from, to } = {}) =>
  authFetch(`/admin/fan-scan${rangeQuery({ from, to })}`, { method: 'GET' });

/** GET /admin/fan-scan/analytics?from=&to= */
export const getFanScanAnalytics = ({ from, to } = {}) =>
  authFetch(`/admin/fan-scan/analytics${rangeQuery({ from, to })}`, { method: 'GET' });

/** GET /admin/fan-scan/questions */
export const listFanScanQuestions = () =>
  authFetch('/admin/fan-scan/questions', { method: 'GET' });

/** GET /admin/fan-scan/questions/:id */
export const getFanScanQuestion = (id) =>
  authFetch(`/admin/fan-scan/questions/${encodeURIComponent(id)}`, { method: 'GET' });

/** PUT /admin/fan-scan/questions/order */
export const updateFanScanQuestionOrder = (questionIds) =>
  authFetch('/admin/fan-scan/questions/order', {
    method: 'PUT',
    body: JSON.stringify({ question_ids: questionIds }),
  });

/** PUT /admin/fan-scan/questions/:id — save copy, weights, image, ratio, presentation, and color */
export const updateFanScanQuestion = (id, body) =>
  authFetch(`/admin/fan-scan/questions/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

/** GET /admin/fan-scan/result-categories */
export const listFanScanResultCategories = () =>
  authFetch('/admin/fan-scan/result-categories', { method: 'GET' });

/** GET /admin/fan-scan/result-categories/:id */
export const getFanScanResultCategory = (id) =>
  authFetch(`/admin/fan-scan/result-categories/${encodeURIComponent(id)}`, { method: 'GET' });

/** PUT /admin/fan-scan/result-categories/:id */
export const updateFanScanResultCategory = (id, body) =>
  authFetch(`/admin/fan-scan/result-categories/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

/** GET /admin/fan-scan/seo */
export const getFanScanSeo = () => authFetch('/admin/fan-scan/seo', { method: 'GET' });

/** PATCH /admin/fan-scan/seo — landing title, description, and canonical path */
export const updateFanScanSeo = (body) =>
  authFetch('/admin/fan-scan/seo', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
